import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AccountLinkPort, IdentityLookup } from './ports/accountLinkPort';
import type { AuthPort, AuthenticatedUser } from './ports/authPort';
import { deriveSessionState, type SessionState } from './sessionState';

/**
 * Holds the session and re-resolves it when the signed-in user changes.
 *
 * *Which screen* is decided by `deriveSessionState`, which is pure and tested
 * without a browser. This hook only handles the async plumbing around it
 * (§5.4 — controllers coordinate, they do not decide).
 */

export interface SessionController {
  readonly state: SessionState;
  readonly user: AuthenticatedUser | null;
  readonly signIn: () => Promise<void>;
  readonly signOut: () => Promise<void>;
  /** Re-runs the lookup, e.g. after confirming or filing a registration. */
  readonly refresh: () => Promise<void>;
  readonly busy: boolean;
  readonly errorAr: string | null;
}

interface Resolution {
  readonly forUid: string | null;
  readonly lookup: IdentityLookup | null;
}

export function useSession(auth: AuthPort, links: AccountLinkPort): SessionController {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [resolution, setResolution] = useState<Resolution>({ forUid: null, lookup: null });
  const [busy, setBusy] = useState(false);
  const [errorAr, setErrorAr] = useState<string | null>(null);

  /**
   * Which user the in-flight lookup belongs to.
   *
   * Without it, signing out and back in as someone else can let a slow reply
   * for the first account land after the second has signed in — and the second
   * person would be shown the first one's result.
   */
  const inFlightFor = useRef<string | null>(null);

  const resolveFor = useCallback(
    async (target: AuthenticatedUser) => {
      inFlightFor.current = target.uid;
      setErrorAr(null);

      try {
        const lookup = await links.lookupMyIdentity();
        if (inFlightFor.current !== target.uid) return;
        setResolution({ forUid: target.uid, lookup });
      } catch (error) {
        if (inFlightFor.current !== target.uid) return;
        setErrorAr(error instanceof Error ? error.message : 'تعذّر إتمام العملية.');
        setResolution({ forUid: target.uid, lookup: null });
      }
    },
    [links],
  );

  useEffect(() => {
    // §11.5.1 — the unsubscribe is not optional. An auth listener outlives
    // every screen, and a leaked one keeps firing against unmounted state.
    return auth.observeUser((next) => {
      setUser(next);

      if (next === null) {
        inFlightFor.current = null;
        setResolution({ forUid: null, lookup: null });
        return;
      }

      // Triggered here rather than in a second effect watching `user`: the
      // lookup belongs to *this* sign-in event, and tying it to the event
      // keeps the two from drifting apart.
      void resolveFor(next);
    });
  }, [auth, resolveFor]);

  const signIn = useCallback(async () => {
    setBusy(true);
    setErrorAr(null);
    try {
      await auth.signInWithGoogle();
    } catch (error) {
      setErrorAr(error instanceof Error ? error.message : 'تعذّر تسجيل الدخول.');
    } finally {
      setBusy(false);
    }
  }, [auth]);

  const signOut = useCallback(async () => {
    await auth.signOut();
  }, [auth]);

  const refresh = useCallback(async () => {
    if (!user) return;
    setBusy(true);
    await resolveFor(user);
    setBusy(false);
  }, [user, resolveFor]);

  const state = useMemo(() => {
    // A resolution belonging to a previous account is not an answer for this
    // one; treating it as `null` keeps the machine in `loading`.
    const lookup = resolution.forUid === user?.uid ? resolution.lookup : null;
    return deriveSessionState(user, lookup);
  }, [user, resolution]);

  return { state, user, signIn, signOut, refresh, busy, errorAr };
}
